import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  Building2, Mail, Lock, Globe, Shield, TrendingUp, Users,
  Home, ChevronRight, BarChart3, FileText, Smartphone,
  ArrowRight, CheckCircle, Star, Phone, MapPin
} from 'lucide-react';
import ForgotPasswordModal from '../components/ForgotPasswordModal';

// ─── Floating background orbs ────────────────────────────────────
const orbs = [
  { size: 500, x: '10%', y: '-10%', color: 'rgba(138,92,246,0.08)' },
  { size: 400, x: '80%', y: '20%', color: 'rgba(138,92,246,0.05)' },
  { size: 350, x: '50%', y: '60%', color: 'rgba(138,92,246,0.04)' },
  { size: 300, x: '-5%', y: '70%', color: 'rgba(138,92,246,0.06)' },
  { size: 450, x: '90%', y: '80%', color: 'rgba(138,92,246,0.05)' },
];

// ─── Feature data ────────────────────────────────────────────────
const features = [
  { icon: Home, title: 'Asset & Unit Tracking', desc: 'Track every property, unit, vehicle, and asset with inventory management and real-time status updates.' },
  { icon: TrendingUp, title: 'Rent Automation', desc: 'Automate rent collection, late fee calculations, and arrears tracking with multi-currency support (KES).' },
  { icon: Users, title: 'Tenant Management', desc: 'End-to-end tenant lifecycle from onboarding and KYC verification to lease renewals and move-outs.' },
  { icon: FileText, title: 'Digital Leases', desc: 'Create, store, and manage lease agreements digitally with automated expiry and renewal alerts.' },
  { icon: BarChart3, title: 'Reports & Analytics', desc: 'Financial reports, occupancy trends, payment histories, and export to Excel/PDF for your accountant.' },
  { icon: Smartphone, title: 'SMS & Notifications', desc: 'Automated SMS reminders for rent, late payment alerts, and tenant communications via the system.' },
];

// ─── Stats ───────────────────────────────────────────────────────
const stats = [
  { value: '99.9%', label: 'Uptime' },
  { value: 'KES', label: 'Local Currency' },
  { value: '24/7', label: 'Support' },
];

// ─── Security badges ─────────────────────────────────────────────
const badges = [
  { icon: Shield, text: 'Enterprise-Grade Encryption' },
  { icon: Shield, text: 'Kenyan Data Centers' },
  { icon: Shield, text: 'Multi-Tenant Isolation' },
];

// ─── Login Section Component ─────────────────────────────────────
function LoginSection({ onSuccess }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    subdomain: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(formData);
      onSuccess();
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="glass-card rounded-3xl p-8 transform transition-all hover:scale-[1.02] duration-300">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
            style={{ background: 'rgba(138,92,246,0.15)' }}>
            <Building2 className="h-8 w-8" style={{ color: '#cbb9ff' }} />
          </div>
          <h2 className="text-3xl font-bold gradient-text">Welcome Back</h2>
          <p className="text-[#b7a9e6] mt-2">Sign in to your property dashboard</p>
        </div>

        {error && (
          <div className="bg-red-900/30 border-l-4 border-red-400 text-red-200 px-4 py-3 rounded mb-6 animate-pulse">
            <i className="fas fa-exclamation-circle mr-2"></i>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-[#d7ccff] text-sm font-bold mb-2">
              <i className="fas fa-building text-[#9b7df0] mr-2"></i>Company Subdomain
            </label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: 'rgba(155,125,240,0.5)' }} />
              <input
                type="text"
                value={formData.subdomain}
                onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
                className="form-input w-full pl-10 px-4 py-3 rounded-lg text-white placeholder-[#6b618f]"
                placeholder="your-company"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-[#d7ccff] text-sm font-bold mb-2">
              <i className="fas fa-envelope text-[#9b7df0] mr-2"></i>Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: 'rgba(155,125,240,0.5)' }} />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="form-input w-full pl-10 px-4 py-3 rounded-lg text-white placeholder-[#6b618f]"
                placeholder="you@example.com"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-[#d7ccff] text-sm font-bold mb-2">
              <i className="fas fa-lock text-[#9b7df0] mr-2"></i>Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: 'rgba(155,125,240,0.5)' }} />
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="form-input w-full pl-10 px-4 py-3 rounded-lg text-white placeholder-[#6b618f]"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                if (!formData.subdomain) {
                  setError('Please enter your company subdomain first');
                  return;
                }
                setError('');
                setShowForgotPassword(true);
              }}
              className="text-sm text-[#b197ff] hover:text-white transition duration-200 flex items-center gap-1"
            >
              <i className="fas fa-key text-xs"></i>
              Forgot Password?
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="cta-button w-full text-white font-bold py-3 px-4 rounded-lg transition duration-200 transform hover:-translate-y-1 shadow-lg relative overflow-hidden"
          >
            <span className="relative z-10">
              <i className="fas fa-sign-in-alt mr-2"></i>
              {loading ? 'Signing in...' : 'Sign In'}
            </span>
          </button>
        </form>
      </div>

      {/* ForgotPasswordModal - Fixed positioning with higher z-index */}
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        subdomain={formData.subdomain}
      />
    </>
  );
}

// =========================================================================
// MAIN LANDING PAGE COMPONENT
// =========================================================================
function Login() {
  const navigate = useNavigate();

  const handleLoginSuccess = () => {
    navigate('/dashboard');
  };

  useEffect(() => {
    document.body.style.overflowX = 'hidden';
    document.body.style.background = '#0a0a0f';
    return () => {
      document.body.style.overflowX = '';
      document.body.style.background = '';
    };
  }, []);

  return (
    <div className="min-h-screen text-white overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #0f0c1f 0%, #1a1533 50%, #0f0c1f 100%)' }}>
      {/* ─── Animated background orbs ───────────────────────────── */}
      {orbs.map((orb, i) => (
        <div
          key={i}
          className="bg-orb"
          style={{
            width: orb.size,
            height: orb.size,
            left: orb.x,
            top: orb.y,
            background: orb.color,
            animation: `float ${6 + i * 2}s ease-in-out infinite`,
            animationDelay: `${i * 0.5}s`,
          }}
        />
      ))}

      {/* ─── Subtle grid overlay ────────────────────────────────── */}
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(138,92,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(138,92,246,0.3) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      {/* ============================================================== */}
      {/* HERO SECTION                                                  */}
      {/* ============================================================== */}
      <header className="relative z-10 min-h-screen flex flex-col">
        {/* ─── Navigation bar ─────────────────────────────────── */}
        <nav className="fixed top-0 left-0 right-0 z-50 glass-card mx-4 mt-4 rounded-2xl px-6 py-3">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="p-1.5 rounded-xl floating" style={{ background: 'rgba(138,92,246,0.15)' }}>
                <img src="/images/favicon.png" alt="Brominon Lease" className="h-7 w-7" />
              </div>
              <span className="text-xl font-bold">
                <span className="text-white">Brominon</span>
                <span className="gradient-text"> Lease</span>
              </span>
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <a href="#features" className="nav-link text-sm font-medium py-2 px-4">Features</a>
              <a href="tel:+254798331960" className="text-sm transition-colors"
                style={{ color: 'rgba(255,255,255,0.5)' }}
                onMouseOver={(e) => e.target.style.color = '#cbb9ff'}
                onMouseOut={(e) => e.target.style.color = 'rgba(255,255,255,0.5)'}>
                +254 798 331 960
              </a>
            </div>
          </div>
        </nav>

        {/* ─── Hero content — vertically centered ─────────────── */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex-1 flex items-center justify-center py-8 pt-32">
          <div className="w-full grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left: Text */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
                style={{ background: 'rgba(138,92,246,0.1)', border: '1px solid rgba(138,92,246,0.2)', color: '#cbb9ff' }}>
                <Star className="h-4 w-4" />
                Trusted by Kenyan Property Managers
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
                <span className="text-white">Advanced Property & </span>
                <span className="gradient-text">Asset Management</span>
                <span className="text-white"> for Kenya</span>
              </h1>

              <p className="text-lg sm:text-xl mb-8 leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Automate rent tracking, tenant onboarding, unit asset inventory, 
                and legal documentation for residential, commercial, and school properties 
                — all in one powerful platform.
              </p>

              {/* Stats row */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-6 sm:gap-10 mb-10">
                {stats.map((s, i) => (
                  <div key={i} className="text-center">
                    <p className="text-2xl sm:text-3xl font-bold gradient-text-purple">{s.value}</p>
                    <p className="text-xs sm:text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Security badges */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                {badges.map((b, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
                    <CheckCircle className="h-3.5 w-3.5" style={{ color: 'rgba(138,92,246,0.5)' }} />
                    {b.text}
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Login Card — centered vertically */}
            <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
              <LoginSection onSuccess={handleLoginSuccess} />
            </div>
          </div>
        </section>
      </header>

      {/* ============================================================== */}
      {/* FEATURES SECTION                                              */}
      {/* ============================================================== */}
      <section id="features" className="relative z-10 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Everything You Need to <span className="gradient-text">Manage Properties</span>
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.5)' }}>
              From single units to large portfolios — Brominon Lease gives you complete control.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="glass-card rounded-2xl p-6 transition-all duration-300 cursor-default hover:border-[#9b7df0]/50"
                  style={{ animationDelay: `${i * 0.1}s` }}>
                  <div className="w-12 h-12 bg-[#1f1b33] rounded-xl flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6" style={{ color: '#cbb9ff' }} />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{f.title}</h3>
                  <p style={{ color: 'rgba(255,255,255,0.5)' }} className="text-sm leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* BROMINON PHILOSOPHY SECTION                                   */}
      {/* ============================================================== */}
      <section className="relative z-10 py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="glass-card rounded-3xl p-8 sm:p-12">
            <div className="w-20 h-20 bg-[#1f1b33] rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Building2 className="h-10 w-10" style={{ color: '#cbb9ff' }} />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 gradient-text">The Brominon Philosophy</h2>
            <p className="text-base sm:text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.6)' }}>
              We believe property managers in Kenya deserve world-class technology. 
              Brominon Lease was built from the ground up for the Kenyan market — 
              supporting CBC and 8-4-4 school housing, KES-based transactions, 
              M-Pesa integration, and the unique multi-unit asset tracking needs 
              of local landlords and institutions.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* FOOTER                                                        */}
      {/* ============================================================== */}
      <footer className="relative z-10 border-t" style={{ borderColor: 'rgba(138,92,246,0.1)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid sm:grid-cols-3 gap-8 mb-8">
            {/* Company info */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="p-1.5 rounded-lg" style={{ background: 'rgba(138,92,246,0.15)' }}>
                  <Building2 className="h-5 w-5" style={{ color: '#cbb9ff' }} />
                </div>
                <span className="font-bold text-white">
                  Autobell <span className="gradient-text">Technologies</span>
                </span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Nairobi, Kenya<br />
                Enterprise property management solutions for the modern Kenyan landlord.
              </p>
            </div>

            {/* Quick links */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
                <li><a href="https://property.autobell.uk" className="hover:text-[#cbb9ff] transition-colors" target="_blank" rel="noopener noreferrer">property.autobell.uk</a></li>
                <li><a href="https://autobell.uk" className="hover:text-[#cbb9ff] transition-colors" target="_blank" rel="noopener noreferrer">autobell.uk</a></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-3 text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4" style={{ color: 'rgba(138,92,246,0.5)' }} />
                  <a href="mailto:autobelltech@proton.me" className="hover:text-[#cbb9ff] transition-colors">autobelltech@proton.me</a>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4" style={{ color: 'rgba(138,92,246,0.5)' }} />
                  <a href="tel:+254798331960" className="hover:text-[#cbb9ff] transition-colors">+254 798 331 960</a>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" style={{ color: 'rgba(138,92,246,0.5)' }} />
                  <span>Nairobi, Kenya</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t text-center text-xs" style={{ borderColor: 'rgba(138,92,246,0.1)', color: 'rgba(255,255,255,0.3)' }}>
            &copy; {new Date().getFullYear()} Autobell Technologies. Brominon Lease v1.0. All rights reserved.
          </div>
        </div>
      </footer>

      <style>{`
        /* Custom CSS matching the HTML design */
        .gradient-text {
          background: linear-gradient(145deg, #ffffff, #cbb9ff, #9b7df0);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .gradient-text-purple {
          background: linear-gradient(145deg, #b197ff, #7b5cf0);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .glass-card {
          background: rgba(18, 18, 30, 0.7);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(138, 92, 246, 0.2);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
        }

        .glass-card:hover {
          border-color: rgba(138, 92, 246, 0.4);
          box-shadow: 0 8px 32px rgba(106, 74, 235, 0.2);
        }

        .form-input {
          background: rgba(19, 18, 30, 0.8);
          border: 1px solid #342f4a;
          transition: all 0.3s;
        }

        .form-input:focus {
          border-color: #9b7df0;
          box-shadow: 0 0 0 3px rgba(155, 125, 240, 0.2);
          outline: none;
        }

        .cta-button {
          background: linear-gradient(135deg, #6d4aff, #8a6cff);
          transition: all 0.3s;
          position: relative;
          overflow: hidden;
        }

        .cta-button::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          transform: translate(-50%, -50%);
          transition: width 0.6s, height 0.6s;
        }

        .cta-button:hover::before {
          width: 300px;
          height: 300px;
        }

        .nav-link {
          position: relative;
          color: #b7a9e6;
          transition: color 0.3s;
        }

        .nav-link:hover {
          color: #ffffff;
        }

        .nav-link::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: 0;
          width: 0;
          height: 2px;
          background: linear-gradient(90deg, #6d4aff, #8a6cff);
          transition: width 0.3s;
        }

        .nav-link:hover::after {
          width: 100%;
        }

        .floating {
          animation: floating 3s ease-in-out infinite;
        }

        @keyframes floating {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }

        .bg-orb {
          position: fixed;
          border-radius: 50%;
          filter: blur(80px);
          pointer-events: none;
          z-index: 0;
          opacity: 0.6;
        }
      `}</style>
    </div>
  );
}

export default Login;